import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { Link, useNavigate } from 'react-router-dom';

const buildNewsBannerBackground = (imageUrl) => {
  const normalizedUrl = (imageUrl || '').trim();
  if (!normalizedUrl) return undefined;

  const escapedUrl = normalizedUrl
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');

  return `linear-gradient(180deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.9)), url("${escapedUrl}")`;
};

function NewsBanner() {
  const [newsList, setNewsList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [progress, setProgress] = useState(100);
  const navigate = useNavigate();

  const SLIDE_DURATION = 5000; // 5 secondi

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const newsQuery = query(
          collection(db, 'news'),
          where('tempoFino', '>=', today),
          orderBy('tempoFino', 'asc')
        );

        const newsSnap = await getDocs(newsQuery);

        const list = newsSnap.docs.map(d => {
          const data = d.data() || {};
          const tempoFino = data.tempoFino?.toDate ? data.tempoFino.toDate() : new Date(data.tempoFino);
          const dataInserimento = data.dataInserimento?.toDate ? data.dataInserimento.toDate() : new Date(data.dataInserimento);

          return {
            id: d.id,
            titolo: data.titolo || '',
            descrizione: data.descrizione || '',
            link: data.link || '',
            imageUrl: data.imageUrl || '',
            tempoFino,
            dataInserimento,
          };
        }).filter(n => n.tempoFino instanceof Date && !isNaN(n.tempoFino.getTime()))
          .sort((a, b) => (b.dataInserimento?.getTime() || 0) - (a.dataInserimento?.getTime() || 0));

        setNewsList(list);
      } catch (err) {
        console.error('Errore caricamento news:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  // Progress bar animation
  useEffect(() => {
    if (newsList.length <= 1 || isHovering) {
      setProgress(100);
      return;
    }

    const intervalTime = 100; // Aggiorna ogni 100ms per fluidità
    const decrement = (intervalTime / SLIDE_DURATION) * 100;

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev <= 0) {
          setCurrentIndex(prevIndex => (prevIndex + 1) % newsList.length);
          return 100;
        }
        return prev - decrement;
      });
    }, intervalTime);

    return () => clearInterval(progressInterval);
  }, [newsList, isHovering]);

  // Reset progress quando cambia slide manualmente
  useEffect(() => {
    setProgress(100);
  }, [currentIndex]);

  if (loading || !newsList.length) return null;

  const currentNews = newsList[currentIndex];
  const hasLink = currentNews.link?.trim().length > 0;
  const hasImage = currentNews.imageUrl?.trim().length > 0;
  const bannerBackgroundImage = buildNewsBannerBackground(currentNews.imageUrl);

  const handleClick = () => {
    if (!hasLink) return;

    const link = currentNews.link.trim();
    try {
      if (link.startsWith('/')) {
        navigate(link);
      } else {
        window.open(link, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.warn('Errore apertura link:', err);
    }
  };

  const handleKey = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && hasLink) {
      e.preventDefault();
      handleClick();
    }
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    //banner cliccabile che reinderizza a newsPage
    <Link to="/news" className="text-decoration-none">
      <div
        className="card shadow-sm mt-4 mb-5"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{ backgroundColor: 'var(--brand-bg-dark)' }}
      >
        {/* Header */}
        <div className="card-header border-0 position-relative">
          <div className="d-flex align-items-center justify-content-between">
            <div
              //testo al centro del banner
              className="d-flex align-items-center justify-content-center w-100">
              <i className="bi bi-newspaper text-brand-secondary me-2"></i>
              <h6 className="mb-0 brand-title">Ultime News</h6>
            </div>
            {newsList.length > 1 && (
              <div className="text-brand-muted small">
                <span>{currentIndex + 1}</span>
                <span className="mx-1">/</span>
                <span>{newsList.length}</span>
              </div>
            )}
          </div>
        </div>

        {/* Contenuto con animazione fade */}
        <div
          className={`card-body p-3 position-relative news-banner-body d-flex flex-column ${hasLink ? 'cursor-pointer' : ''} ${hasImage ? 'news-banner-body--with-image' : ''}`}
          onClick={handleClick}
          onKeyDown={handleKey}
          role={hasLink ? "button" : "article"}
          tabIndex={hasLink ? 0 : -1}
          style={
            hasImage
              ? {
                backgroundImage: bannerBackgroundImage,
              }
              : undefined
          }
        >
          {/* Timer bar (invariata) */}
          {newsList.length > 1 && (
            <div className="position-absolute bottom-0 start-0 end-0" style={{ height: '2px' }}>
              <div
                className="news-timer-bar h-100"
                style={{
                  width: `${progress}%`,
                  backgroundColor: 'var(--brand-secondary)'
                }}
              ></div>
            </div>
          )}

          {/* Contenitore titolo: si espande per spingere la riga inferiore in basso */}
          <div className="mb-2 pt-1 position-relative news-banner-content flex-grow-1">
            <h6 className="mb-1 fw-semibold news-title justify-content-center d-flex align-items-center text-center">
              {currentNews.titolo}
            </h6>
          </div>

          {/* Data e pallini: ora ancorati alla fine */}
          <div className="d-flex justify-content-between align-items-center position-relative news-banner-content mt-auto">
            <div className={`small ${hasImage ? 'text-white-50' : 'text-muted-brand'}`}>
              <i className="bi bi-calendar3 me-1"></i>
              {currentNews.dataInserimento
                ? currentNews.dataInserimento.toLocaleDateString('it-IT', {
                  day: '2-digit',
                  month: 'short',
                })
                : ''}
            </div>

            {newsList.length > 1 && (
              <div className="d-flex gap-1 align-items-center">
                {newsList.map((_, idx) => (
                  <button
                    key={idx}
                    className={`news-indicator-dot ${idx === currentIndex ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToSlide(idx);
                    }}
                    aria-label={`Vai alla news ${idx + 1}`}
                    aria-current={idx === currentIndex ? 'true' : 'false'}
                  >
                    •
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default NewsBanner;
